import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, AlertTriangle, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { useScope } from "@/hooks/use-scope";

import { ORDER_STATUSES, type OrderStatus } from "@/lib/orders/state-machine";
import {
  getActiveWorkflow,
  upsertWorkflowTransition,
  listCanonicalTransitions,
  upsertWorkflowHoldReason,
} from "@/lib/workflows.functions";

export const Route = createFileRoute("/_authenticated/app/admin/workflows")({
  component: WorkflowsAdmin,
});

function WorkflowsAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const getActive = useServerFn(getActiveWorkflow);
  const getCanonical = useServerFn(listCanonicalTransitions);
  const upsert = useServerFn(upsertWorkflowTransition);
  const upsertHold = useServerFn(upsertWorkflowHoldReason);

  const activeQ = useQuery({
    queryKey: ["workflow", "active", tenantId],
    queryFn: () => getActive({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });
  const canonicalQ = useQuery({
    queryKey: ["workflow", "canonical"],
    queryFn: () => getCanonical(),
  });

  const canonicalSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of canonicalQ.data?.rows ?? []) s.add(`${r.from_status}>${r.to_status}`);
    return s;
  }, [canonicalQ.data]);

  const transitionMap = useMemo(() => {
    const m = new Map<string, { id: string; is_enabled: boolean; requires_role: string | null; requires_reason: boolean }>();
    for (const t of activeQ.data?.transitions ?? []) {
      m.set(`${t.from_status}>${t.to_status}`, {
        id: t.id,
        is_enabled: t.is_enabled,
        requires_role: t.requires_role,
        requires_reason: t.requires_reason,
      });
    }
    return m;
  }, [activeQ.data]);

  const toggle = useMutation({
    mutationFn: async (vars: { from: string; to: string; enabled: boolean }) => {
      return await upsert({
        data: {
          workflowId: activeQ.data!.workflow!.id,
          fromStatus: vars.from,
          toStatus: vars.to,
          isEnabled: vars.enabled,
          requiresReason: false,
          requiresRole: null,
        },
      });
    },
    onSuccess: (res) => {
      if (res.ok) {
        toast.success("Transition updated");
        qc.invalidateQueries({ queryKey: ["workflow", "active", tenantId] });
      } else if (res.reason === "non_canonical_transition") {
        toast.error("Blocked by canonical state machine — this transition can never be enabled.");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateHold = useMutation({
    mutationFn: async (vars: {
      id: string;
      requiresApproval?: boolean;
      autoReleaseAfter?: string | null;
      isEnabled?: boolean;
    }) => upsertHold({ data: vars }),
    onSuccess: () => {
      toast.success("Hold reason updated");
      qc.invalidateQueries({ queryKey: ["workflow", "active", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!tenantId) {
    return (
      <div className="p-6">
        <PageHeader title="Order Workflows" description="Configure per-tenant transitions, hold reasons, and automation hooks." />
        <Card className="p-6">Select a tenant in the scope picker to manage its workflow.</Card>
      </div>
    );
  }

  const loading = activeQ.isLoading || canonicalQ.isLoading;
  const wf = activeQ.data?.workflow;

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Order Workflows"
        description="Per-tenant overrides on top of the canonical order state machine."
      />
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading workflow…
        </div>
      )}
      {!loading && !wf && (
        <Card className="p-6 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-warning" /> No active workflow for this tenant.
        </Card>
      )}
      {wf && (
        <>
          <Card className="p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">{wf.name}</div>
              <div className="text-xs text-muted-foreground">
                Version {wf.version} · {wf.description ?? "—"}
              </div>
            </div>
            <Badge variant="outline" className="gap-1">
              <ShieldCheck className="h-3 w-3" /> Canonical backstop active
            </Badge>
          </Card>
          <Tabs defaultValue="transitions">
            <TabsList>
              <TabsTrigger value="transitions">Transitions</TabsTrigger>
              <TabsTrigger value="holds">Hold reasons</TabsTrigger>
              <TabsTrigger value="audit">Audit log</TabsTrigger>
            </TabsList>

            <TabsContent value="transitions">
              <Card className="p-4 overflow-x-auto">
                <p className="text-xs text-muted-foreground mb-3">
                  Toggle which transitions this tenant allows. Cells marked
                  <span className="mx-1 inline-flex items-center"><Badge variant="secondary">canonical-forbidden</Badge></span>
                  cannot be enabled — the database enforces this independently.
                </p>
                <table className="text-xs border-separate border-spacing-1 min-w-max">
                  <thead>
                    <tr>
                      <th className="text-left p-1 text-muted-foreground">From → To</th>
                      {ORDER_STATUSES.map((s) => (
                        <th key={s} className="p-1 font-medium text-left whitespace-nowrap">
                          {s}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ORDER_STATUSES.map((from) => (
                      <tr key={from}>
                        <td className="p-1 font-medium whitespace-nowrap">{from}</td>
                        {ORDER_STATUSES.map((to) => {
                          const k = `${from}>${to}`;
                          const canonical = canonicalSet.has(k);
                          const tr = transitionMap.get(k);
                          if (from === to) {
                            return <td key={to} className="p-1 text-center text-muted-foreground">—</td>;
                          }
                          if (!canonical) {
                            return (
                              <td key={to} className="p-1 text-center text-muted-foreground/60" title="Forbidden by canonical state machine">
                                ✕
                              </td>
                            );
                          }
                          return (
                            <td key={to} className="p-1 text-center">
                              <Switch
                                checked={!!tr?.is_enabled}
                                onCheckedChange={(v) =>
                                  toggle.mutate({ from, to, enabled: v })
                                }
                                disabled={toggle.isPending}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </TabsContent>

            <TabsContent value="holds">
              <Card className="p-4">
                <p className="text-xs text-muted-foreground mb-3">
                  Inline edits write to <code>order_workflow_hold_reasons</code>. The workflow
                  runner reads from this table at hold-evaluation time — no policy logic in this UI.
                </p>
                <table className="text-sm w-full">
                  <thead>
                    <tr className="text-left text-muted-foreground text-xs">
                      <th className="py-1">Code</th>
                      <th className="py-1">Label</th>
                      <th className="py-1">Kind</th>
                      <th className="py-1">Approval</th>
                      <th className="py-1">Auto release after</th>
                      <th className="py-1">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeQ.data?.holdReasons ?? []).map((h) => (
                      <tr key={h.id} className="border-t">
                        <td className="py-1 font-mono text-xs">{h.code}</td>
                        <td className="py-1">{h.label}</td>
                        <td className="py-1">{h.kind}</td>
                        <td className="py-1">
                          <Switch
                            checked={!!h.requires_approval}
                            disabled={updateHold.isPending}
                            onCheckedChange={(v) =>
                              updateHold.mutate({ id: h.id, requiresApproval: v })
                            }
                          />
                        </td>
                        <td className="py-1">
                          <Input
                            className="h-7 w-32 text-xs"
                            placeholder="e.g. 60 minutes"
                            defaultValue={h.auto_release_after ?? ""}
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              const prev = h.auto_release_after ?? "";
                              if (next === prev) return;
                              updateHold.mutate({
                                id: h.id,
                                autoReleaseAfter: next || null,
                              });
                            }}
                          />
                        </td>
                        <td className="py-1">
                          <Switch
                            checked={!!h.is_enabled}
                            disabled={updateHold.isPending}
                            onCheckedChange={(v) =>
                              updateHold.mutate({ id: h.id, isEnabled: v })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card className="p-4 text-sm text-muted-foreground">
                Audit timeline is exposed per-order on the order detail page (Phase 3 WS1 follow-up wires this UI).
                Every transition, hold, and automation fire already writes a row to <code>order_workflow_audit</code>.
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}